/**
 * The sources on the Sources read tab (DOP-623): one row per file the run read,
 * with how it was read in words; opening a row lists every page or cell the
 * memo cites from it, one line each — numbered with the memo's own source numbers, so "4" names
 * one page everywhere — with the passage and what in the memo uses it.
 *
 * An unreadable or excluded file is NAMED here: v1 has no OCR, and a file
 * silently skipped is a question nobody knows to ask. A cited source whose
 * file is not in the run's list (a web page) gets a row of its own.
 *
 * Files and web pages are listed apart and marked as what they are
 * (DOP-645): a web page names its site and the day it was read.
 */

import { ChevronRight, FileText, Globe } from "lucide-react";
import React, { useState } from "react";

import type {
	DealScreeningFileRead,
	DealScreeningReadStatus,
	DealScreeningSource,
} from "../../types/dealScreening";
import { CARD } from "./checkStyles";
import { countLabel, formatIsoDay } from "./dealScreeningFormat";
import type { SourceTarget, SourceUse } from "./memoModel";

export interface SourcesViewProps {
	files: DealScreeningFileRead[];
	sources: SourceUse[];
	/** The memo's number for a cited place; null when the memo does not number it. */
	numberOf: (anchor: string | null | undefined) => number | null;
	onOpenSource: (source: DealScreeningSource) => void;
	/** Shows where on the memo a cited place is used (Turtleback 10-03). */
	onJump?: (target: SourceTarget) => void;
	folderName?: string | null;
}

const READ: Record<DealScreeningReadStatus, { label: string; className: string; means: string }> = {
	text: {
		label: "Read",
		className: "bg-[#ecfdf3] text-[#067647]",
		means: "Its text was extracted and every page or sheet was searched.",
	},
	ocr: {
		label: "Read by OCR",
		className: "bg-[#fffaeb] text-[#b54708]",
		means: "A scan; its text was recognised, so figures from it carry an OCR mark.",
	},
	unreadable: {
		label: "Unreadable",
		className: "bg-[#fef3f2] text-[#b42318]",
		means: "No text layer and no OCR, so nothing in it reached the memo.",
	},
	skipped: {
		label: "Excluded",
		className: "bg-[#f2f2f2] text-[#5B6472]",
		means: "Kept out of the evidence on purpose; the note says why.",
	},
};

/** How a web page was read: fetched, not opened from the folder (DOP-645). */
const READ_WEB = {
	...READ.text,
	means: "A web page the run searched for; its text was fetched and read.",
};

const isWeb = (file: DealScreeningFileRead): boolean => file.doc_type === "web";

/** "zoominfo.com" out of a page's address, or null. */
const siteOf = (url: string | null | undefined): string | null => {
	if (!url) return null;
	try {
		return new URL(url).hostname.replace(/^www\./, "");
	} catch {
		return null;
	}
};

/** "read Sep 29, 2026" out of the time a page was fetched. */
const readOn = (note: string | null): string | null => {
	if (!note) return null;
	return /^\d{4}-\d{2}-\d{2}/.test(note) ? `read ${formatIsoDay(note.slice(0, 10))}` : note;
};

const where = (source: DealScreeningSource): string =>
	source.sheet && source.cell
		? `${source.sheet}!${source.cell}`
		: source.page !== null
			? `p. ${source.page}`
			: "—";

const fileExtent = (file: DealScreeningFileRead): string | null =>
	file.pages > 0
		? countLabel(file.pages, "page")
		: file.sheets > 0
			? countLabel(file.sheets, "sheet")
			: null;

const byNumber =
	(numberOf: SourcesViewProps["numberOf"]) =>
	(a: SourceUse, b: SourceUse): number =>
		(numberOf(a.key) ?? Number.MAX_SAFE_INTEGER) - (numberOf(b.key) ?? Number.MAX_SAFE_INTEGER);

const SourcesView: React.FC<SourcesViewProps> = ({
	files,
	sources,
	numberOf,
	onOpenSource,
	onJump,
	folderName = null,
}) => {
	const byFile = new Map<string, SourceUse[]>();
	sources.forEach((use) => {
		const id = String(use.source.attachment_id);
		byFile.set(id, [...(byFile.get(id) ?? []), use]);
	});
	const listed = new Set(files.map((file) => String(file.attachment_id)));
	// Cited places whose file the run did not list (a web page), by name.
	const others = new Map<string, SourceUse[]>();
	sources
		.filter((use) => !listed.has(String(use.source.attachment_id)))
		.forEach((use) => {
			const name = use.source.title || use.source.filename;
			others.set(name, [...(others.get(name) ?? []), use]);
		});

	const [open, setOpen] = useState<Set<string>>(new Set());
	const toggle = (id: string) =>
		setOpen((current) => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	/** One line per cited place: number, where, the passage, what uses it. */
	const cited = (uses: SourceUse[]) => (
		<ul className="border-t border-[#f1f2f4] bg-[#fcfcfb] py-1">
			{[...uses].sort(byNumber(numberOf)).map((use) => {
				const number = numberOf(use.key);
				return (
					<li
						key={use.key}
						className="grid grid-cols-[28px_88px_minmax(0,1fr)] items-baseline gap-x-3 px-5 py-1.5 md:grid-cols-[28px_88px_minmax(0,1fr)_minmax(0,220px)]"
					>
						<span className="flex justify-center">
							{number !== null && (
								<span
									className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded bg-[#E6F3F3] px-1 text-[10.5px] font-semibold text-[#004C4B]"
									aria-label={`Source ${number}`}
								>
									{number}
								</span>
							)}
						</span>
						<button
							type="button"
							onClick={() => onOpenSource(use.source)}
							className="truncate text-left text-[13px] font-semibold text-[#004C4B] underline-offset-2 hover:underline"
						>
							{where(use.source)}
						</button>
						<span
							className="truncate font-serif text-[13.5px] text-[#3f4247]"
							title={use.source.quote || undefined}
						>
							{use.source.quote ? `“${use.source.quote}”` : "—"}
						</span>
						<span
							className="col-start-3 truncate text-xs text-[#6B7280] md:col-start-auto md:text-right"
							title={use.uses.join(" · ")}
						>
							{/* Each use is a way to the place on the memo that cites this. */}
							{onJump
								? use.targets.map((target, index) => (
										<React.Fragment key={target.label}>
											{index > 0 && " · "}
											<button
												type="button"
												onClick={() => onJump(target)}
												title={`Show ${target.label} in the memo`}
												className="text-[#004C4B] underline-offset-2 hover:underline"
												data-testid="source-use"
											>
												{target.label}
											</button>
										</React.Fragment>
									))
								: use.uses.join(" · ")}
							{use.source.ocr ? " · OCR" : ""}
							{use.source.verified ? "" : " · not verified"}
						</span>
					</li>
				);
			})}
		</ul>
	);

	/** A file's row: name, extent, how many places are cited, how it was read. */
	const row = (
		id: string,
		name: string,
		meta: (string | null)[],
		uses: SourceUse[],
		status: (typeof READ)[DealScreeningReadStatus] | null,
		web = false,
		/** A web page's address: a page the memo cites nothing from still opens. */
		href: string | null = null,
	) => {
		const Kind = web ? Globe : FileText;
		const expanded = open.has(id);
		const canOpen = uses.length > 0;
		// Every page read opens (Turtleback 10-03): a cited one unfolds to its
		// cited places, an uncited one goes straight to the page.
		const link = !canOpen && href ? href : null;
		const body = (
			<>
					<ChevronRight
						className={`h-4 w-4 shrink-0 text-[#9a9ea3] transition-transform ${
							expanded ? "rotate-90" : ""
						} ${canOpen ? "" : "invisible"}`}
						aria-hidden
					/>
					<Kind
						className="h-4 w-4 shrink-0 text-[#6B7280]"
						aria-label={web ? "Web page" : "File"}
						role="img"
					/>
					<span className="min-w-0 flex-1 truncate text-sm font-medium text-[#111111]">{name}</span>
					<span className="hidden shrink-0 text-[12.5px] text-[#6B7280] sm:inline">
						{meta.filter(Boolean).join(" · ")}
					</span>
					<span className="w-[92px] shrink-0 text-right text-[12.5px] text-[#5B6472]">
						{uses.length > 0 ? `${countLabel(uses.length, "place")} cited` : "Not cited"}
					</span>
					<span className="flex w-[104px] shrink-0 justify-end">
						{status && (
							<span
								title={status.means}
								className={`inline-flex h-[22px] items-center rounded-full px-2 text-[11.5px] font-semibold ${status.className}`}
							>
								{status.label}
							</span>
						)}
					</span>
			</>
		);
		return (
			<li key={id} aria-label={name} role="region">
				{link ? (
					<a
						href={link}
						target="_blank"
						rel="noopener noreferrer"
						title="Open the page"
						className="flex w-full items-center gap-3 px-5 py-2.5 text-left hover:bg-[#fafaf8]"
					>
						{body}
					</a>
				) : (
					<button
						type="button"
						onClick={() => canOpen && toggle(id)}
						aria-expanded={canOpen ? expanded : undefined}
						disabled={!canOpen}
						className="flex w-full items-center gap-3 px-5 py-2.5 text-left enabled:hover:bg-[#fafaf8]"
					>
						{body}
					</button>
				)}
				{expanded && cited(uses)}
			</li>
		);
	};

	const folderFiles = files.filter((file) => !isWeb(file));
	const webPages = files.filter(isWeb);
	const webCount = webPages.length + others.size;
	/** A group's heading inside the list, once there are two kinds to tell apart. */
	const group = (label: string, count: number) =>
		folderFiles.length > 0 && webCount > 0 ? (
			<li
				key={`group:${label}`}
				className="bg-[#fafaf8] px-5 py-1.5 text-[11px] font-semibold uppercase tracking-[.06em] text-[#6B7280]"
			>
				{label} · {count}
			</li>
		) : null;

	return (
		<section aria-label="What was read" className={CARD}>
			<header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[#eef0f2] px-5 py-3">
				<h2 className="text-sm font-semibold text-[#111111]">What was read</h2>
				<p className="text-[12.5px] text-[#6B7280]">
					{countLabel(folderFiles.length, "file")}
					{folderName ? ` in Deal Room › ${folderName}/` : ""}
					{webCount > 0 ? ` · ${countLabel(webCount, "web page")} searched` : ""} · open one to
					see what the memo cites from it
				</p>
			</header>
			<ul className="divide-y divide-[#f1f2f4]" data-testid="files-read">
				{group("Files", folderFiles.length)}
				{folderFiles.map((file) =>
					row(
						String(file.attachment_id),
						file.filename,
						[fileExtent(file), file.doc_date ? `dated ${formatIsoDay(file.doc_date)}` : null, file.note],
						byFile.get(String(file.attachment_id)) ?? [],
						READ[file.read_status],
					),
				)}
				{group("Web pages", webCount)}
				{webPages.map((page) => {
					const uses = byFile.get(String(page.attachment_id)) ?? [];
					const url = page.url ?? uses.find((use) => use.source.url)?.source.url;
					return row(
						String(page.attachment_id),
						page.filename,
						[siteOf(url), readOn(page.note)],
						uses,
						page.read_status === "text" ? READ_WEB : READ[page.read_status],
						true,
						url ?? null,
					);
				})}
				{Array.from(others.entries()).map(([name, uses]) =>
					row(`other:${name}`, name, [siteOf(uses.find((use) => use.source.url)?.source.url)], uses, null, true),
				)}
			</ul>
		</section>
	);
};

export default SourcesView;
